
import { EventBus } from "@/plugins/eventBus"
import axios, { AxiosError, AxiosResponse } from "axios"
import Vue from "vue"
import EmailRulesMixin from "@/mixins/EmailRulesMixin.vue"
import { validateEmail } from "@/utils/emailAddress"
import { state, mutations } from "@/plugins/state"
import Locale from "@/components/Locale.vue"
import {
  postLoginRedirectTarget,
} from "@/utils/listParagonRouteQuery"
import type { RouteQuery } from "@/utils/listParagonRouteQuery"

export default Vue.extend({
  name: "Login",
  mixins: [EmailRulesMixin],
  data: () => ({
    textError: "",
    email: "",
    password: "",
    token: state.token,
    show: false,
    ssoLoading: "" as "" | "google" | "microsoft",
    googleSsoEnabled: true,
    microsoftSsoEnabled: true,
  }),
  components: {
    Locale
  },
  mounted() {
    const token = this.$route.query.token as string
    const expires = this.$route.query.expires as string
    if (token && expires) {
      mutations.login(token)
      this.navigateAfterLogin()
      mutations.resetSpaces()
      return
    }
    const ssoError = this.$route.query.sso_error as string
    const message = this.$route.query.message as string
    if (ssoError) {
      const msg = this.getLocalizedSsoErrorMessage(ssoError, message)
      EventBus.$emit("snackbar", { message: msg, color: "error" })
      this.$router.replace({ path: "/login", query: {} }).catch(() => undefined)
    }
  },
  methods: {
    navigateAfterLogin(): void {
      const target = postLoginRedirectTarget(this.$route.query as RouteQuery) ?? '/home'
      this.$router.replace(target).catch(() => undefined)
    },
    login() {
      const form = this.$refs.loginForm as { validate?: () => boolean } | undefined
      if (form?.validate && !form.validate()) {
        return
      }
      axios
        .post("/user/login", {
          email: this.email,
          password: this.password,
        })
        .then((response: AxiosResponse) => {
          const token = response.data["token"] as string
          this.token = token
          mutations.login(token)
          this.navigateAfterLogin()
          mutations.resetSpaces()
        })
        .catch((error: AxiosError) => {
          EventBus.$emit('snackbar',{axiosError: error})
        })
    },
    async loginWithGoogle() {
      this.ssoLoading = "google"
      try {
        const params = validateEmail(this.email) === null ? { email: this.email } : {}
        const response = await axios.get("/user/login/google/url", { params })
        const url = response.data?.url
        if (url) {
          globalThis.location.href = url
        } else {
          EventBus.$emit("snackbar", { message: this.$vuetify.lang.t("$vuetify.google_oauth_url_error"), color: "error" })
        }
      } catch (error) {
        EventBus.$emit("snackbar", { axiosError: error })
      } finally {
        this.ssoLoading = ""
      }
    },
    getLocalizedSsoErrorMessage(ssoError: string, message: string): string {
      const messageToKey: Record<string, string> = {
        "No Dispojob account linked to this Google account": "sso_error_no_account_google",
        "No Dispojob account linked to this Microsoft account": "sso_error_no_account_microsoft",
      }
      const translationKey = message && messageToKey[message]
      if (translationKey) {
        return this.$vuetify.lang.t("$vuetify.login_page." + translationKey)
      }
      return (
        message ||
        this.$vuetify.lang.t("$vuetify.login_page.sso_error_" + ssoError) ||
        this.$vuetify.lang.t("$vuetify.login_page.sso_error_generic")
      )
    },
    async loginWithMicrosoft() {
      this.ssoLoading = "microsoft"
      try {
        const params = validateEmail(this.email) === null ? { email: this.email } : {}
        const response = await axios.get("/user/login/azure/url", { params })
        const url = response.data?.url
        if (url) {
          globalThis.location.href = url
        } else {
          EventBus.$emit("snackbar", { message: this.$vuetify.lang.t("$vuetify.microsoft_oauth_url_error"), color: "error" })
        }
      } catch (error) {
        EventBus.$emit("snackbar", { axiosError: error })
      } finally {
        this.ssoLoading = ""
      }
    },
  }
})
